// src/components/frontpage/columns/GroupChatColumn.jsx
//
// Group-context Chat column for the Group desktop front page (Groups Tier 1).
//
// The mauve-context sibling of ChatColumn. Identical engine (<ConcertinaThread variant="chat">)
// and chrome; the ONLY difference is the data adapters: chat creation and replies carry
// community_id = group.id (group-scoped) and never resolve home_locality_id. The locality
// ChatColumn is left untouched (Option A — thin group wrappers, no edits to shared columns).
//
// Write-scope is the load-bearing fix: createOriginalChat persists community_id and only
// attaches home_locality_id when explicitly passed (we never pass it), so a chat created here
// is a GROUP chat, not a locality one; createResponse persists the same community_id, so a
// reply carries the group's id rather than the null/null orphan the locality adapter produced.
//
// Only members may write here (chats INSERT policy "Members write where they can read"). A
// non-member sees no "+" and, since group chat is members-only to read too, the empty state
// says why instead of inviting a post the database would refuse.

import React, { useCallback } from 'react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAuth } from '../../../contexts/AuthContext';
import { chatService } from '../../../data/dataService';
import ConcertinaThread from '../../chat/ConcertinaThread';
import ModeColumn from './ModeColumn';

const GroupChatColumn = ({
  data = [],
  group,
  openThreadId = null,
  onThreadToggle,
  onChatsRefresh,
  hideColumnHeader = false,
  canWrite = false        // group members only; the database refuses anyone else (42501)
}) => {
  const theme = useTheme();
  const { t: tFp } = useTranslation('frontpage');
  const { requireAuth, currentUser } = useAuth();

  const communityId = group?.id || null;

  // --- Group data adapters for the engine ---
  const loadResponses = useCallback((rootChatId) => chatService.getResponsesByRoot(rootChatId), []);

  const onCreateTopLevel = useCallback(async ({ headline, content }) => {
    await chatService.createOriginalChat({
      headline,
      content,
      community_id: communityId,   // group-scoped — never null, never home_locality_id
      author_id: currentUser.id
    });
  }, [communityId, currentUser]);

  const onCreateReply = useCallback(async ({ parentChatId, content }) => {
    await chatService.createResponse({
      content,
      author_id: currentUser.id,
      parent_chat_id: parentChatId,
      community_id: communityId    // group-scoped — never null/null orphan
    });
  }, [communityId, currentUser]);

  const handleNewChat = () => {
    requireAuth('gate_body_create_chat', () => {
      window.dispatchEvent(new CustomEvent('ournet:open-chat-composer'));
    });
  };

  return (
    <ModeColumn
      title={tFp('column_chat')}
      themeColor={theme.primary}
      hideColumnHeader={hideColumnHeader}
      onAdd={canWrite ? handleNewChat : undefined}
      addLabel={tFp('new_chat')}
      scrollBody={false}
    >
      <ConcertinaThread
        variant="chat"
        data={data}
        loadResponses={loadResponses}
        onCreateTopLevel={onCreateTopLevel}
        onCreateReply={onCreateReply}
        onRefresh={onChatsRefresh}
        openThreadId={openThreadId}
        onThreadToggle={onThreadToggle}
        reportContentType="chat"
        emptyState={canWrite ? tFp('empty_chat') : tFp('group_chat_members_only')}
      />
    </ModeColumn>
  );
};

export default GroupChatColumn;
